import { databaseService } from '@/services/databaseService';
import { reserveSyncExecution } from './SyncExecutionLease';
import { getSyncClientBuild } from './SyncCanaryFeatureService';
import { capturePendingSyncSession, assertPendingSyncSessionCurrent, createPendingSyncSupabaseClient,
  isPendingSyncSessionSnapshotStateCurrent } from './PendingSyncSession';
import { authorizeChecklistCancellation, discoverChecklistCancellations, listChecklistCancellations,
  cancellationBlocksOperation, processEligibleChecklistCancellations,
  type CancellationRow, type CancellationOperation } from './ChecklistCancellation';
import type { PendingSyncSessionSnapshot } from './PendingSyncSession';

export const prepareChecklistCancellations = (company: string) => discoverChecklistCancellations(databaseService, company);
export const isChecklistCancellationBlocked = async (company: string, operation: any) => {
  const rows = await listChecklistCancellations(databaseService, company);
  const tombstones = await databaseService.listItems<any>('sync_tombstones', { empresa_id: company });
  const filtering = rows.filter(row => row.status !== 'cancellation_remote_exists' || !tombstones.some(t =>
    t.empresa_id === company && t.entity_type === 'guincho_checklists' && t.entity_id === row.item_id));
  return cancellationBlocksOperation(filtering, { ...operation, item_id: operation.item_id ?? operation.itemId } as CancellationOperation);
};
export const assertChecklistNotCancelled = async (company: string, checklistId: string) => {
  const rows = await prepareChecklistCancellations(company);
  if (rows.some(row => row.item_id === checklistId)) {
    throw new Error('Checklist com cancelamento pendente de autorização; criação e mídia preservadas.');
  }
};
export const reviewChecklistCancellations = async (): Promise<CancellationRow[]> => {
  const session = await capturePendingSyncSession();
  if (!session) throw new Error('Sessão não validada.');
  const rows = await prepareChecklistCancellations(session.empresaId);
  await assertPendingSyncSessionCurrent(session);
  return rows.filter(row => row.status !== 'resolved' && row.status !== 'cancellation_remote_exists');
};
export const confirmChecklistCancellation = async (row: CancellationRow) => {
  const release = reserveSyncExecution();
  if (!release) throw new Error('Aguarde a sincronização terminar.');
  try {
    const session = await capturePendingSyncSession();
    if (!session || session.empresaId !== row.empresa_id) throw new Error('Empresa ou sessão mudou.');
    return await authorizeChecklistCancellation({ db: databaseService, row,
      current: () => isPendingSyncSessionSnapshotStateCurrent(session),
      request: async (manifest, digest) => {
        await assertPendingSyncSessionCurrent(session);
        const { data, error } = await createPendingSyncSupabaseClient(session).rpc('cancel_absent_checklist_v1', {
          p_manifest: manifest, p_manifest_sha512: digest, p_client_build: getSyncClientBuild(),
        });
        if (error) throw error;
        return data;
      },
    });
  } finally { release(); }
};

export const processAutomaticChecklistCancellations = async (session: PendingSyncSessionSnapshot) => {
  if (getSyncClientBuild() < 124) return { cancelled: 0, remoteExists: 0, failed: 0 };
  await assertPendingSyncSessionCurrent(session);
  const client = createPendingSyncSupabaseClient(session);
  return processEligibleChecklistCancellations({
    db: databaseService,
    companyId: session.empresaId,
    current: () => isPendingSyncSessionSnapshotStateCurrent(session),
    request: async (manifest, digest) => {
      await assertPendingSyncSessionCurrent(session);
      const { data, error } = await client.rpc('cancel_absent_checklist_v1', {
        p_manifest: manifest,
        p_manifest_sha512: digest,
        p_client_build: getSyncClientBuild(),
      });
      if (error) throw error;
      return data;
    },
  });
};
